import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  Panel,
  addEdge,
  useNodesState,
  useEdgesState,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  Box,
  Typography,
  Button,
  TextField,
  Stack,
  Paper,
  IconButton,
  Divider,
} from '@mui/material';
import { ArrowBack as BackIcon, Save as SaveIcon } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { agentFlowsApi } from '../../api/agentFlows';
import {
  migrateFlowConfig,
  buildFlowConfigForSave,
  defaultLayerForNodeType,
  enrichNodeData,
} from '../../api/agentFlowSchema';
import { useToast } from '../../hooks/useToast';
import { agentFlowNodeTypes } from './flowNodeTypes';
import { AgentFlowTriggerContextPanel } from './AgentFlowTriggerContextPanel';
import { AgentFlowNodeProperties } from './AgentFlowNodeProperties';
import { topBarHeight } from '../../components/layout/kobraLayoutTokens';

const K = { navy: '#121826', blue: '#4185F1', border: 'rgba(18, 24, 38, 0.12)' };

const kobraFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 1.5,
    backgroundColor: '#fff',
    '& fieldset': { borderColor: 'rgba(18, 24, 38, 0.1)' },
    '&:hover fieldset': { borderColor: `${K.blue}55` },
    '&.Mui-focused fieldset': { borderColor: K.blue, borderWidth: 2 },
  },
};

const kobraPrimaryBtnSx = {
  '&&': {
    borderRadius: 1.5,
    px: 2.5,
    py: 1,
    textTransform: 'none',
    fontWeight: 700,
    bgcolor: K.blue,
    color: '#fff',
    border: 'none',
    backgroundImage: 'none',
    boxShadow: `0 4px 14px ${alpha(K.blue, 0.35)}`,
    '&:hover': {
      bgcolor: '#3574d9',
      border: 'none',
      backgroundImage: 'none',
    },
  },
};

const newNodeId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `n_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

function AgentFlowEditorInner() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  const [name, setName] = useState('');
  const [flowMeta, setFlowMeta] = useState(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedId, setSelectedId] = useState(null);
  const didInitForFlowId = useRef(null);

  const { data: flow, isLoading, isSuccess } = useQuery({
    queryKey: ['agentFlow', id],
    queryFn: () => agentFlowsApi.getFlow(id),
    enabled: !!id,
  });

  useLayoutEffect(() => {
    if (!flow || flow.id !== id || didInitForFlowId.current === flow.id) return;
    didInitForFlowId.current = flow.id;
    setName(flow.name || '');
    const fc = migrateFlowConfig(flow.flow_config || {});
    setFlowMeta(fc.meta);
    setNodes(fc.nodes?.length ? fc.nodes : []);
    setEdges(fc.edges?.length ? fc.edges : []);
  }, [id, flow, setNodes, setEdges]);

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge({ ...params, type: 'smoothstep' }, eds)),
    [setEdges]
  );

  const onSelectionChange = useCallback(({ nodes: sel }) => {
    setSelectedId(sel?.[0]?.id ?? null);
  }, []);

  const selectedNode = nodes.find((n) => n.id === selectedId) || null;

  const updateSelectedData = (patch) => {
    if (!selectedId) return;
    setNodes((nds) =>
      nds.map((n) => (n.id === selectedId ? { ...n, data: { ...n.data, ...patch } } : n))
    );
  };

  const addNode = (type) => {
    const nid = newNodeId();
    const y = 80 + nodes.length * 24;
    const position = { x: 160 + (nodes.length % 4) * 40, y };
    let data = { layer: defaultLayerForNodeType(type) };
    if (type === 'message') {
      data = {
        ...data,
        message: 'Texto del mensaje',
        actionKind: 'dynamic_message',
        templateName: '',
        noteBody: '',
        reminderLabel: '',
        blockDurationHours: '',
      };
    }
    if (type === 'condition') {
      data = { ...data, expression: 'Condición', logicKind: 'simple' };
    }
    if (type === 'end') {
      data = { ...data, label: 'Fin', outcomeKey: '', outcomeCategory: 'other' };
    }
    const node = enrichNodeData({ id: nid, type, position, data });
    setNodes((nds) => [...nds, node]);
  };

  const removeSelectedNode = () => {
    if (!selectedNode || selectedNode.type === 'start') return;
    setNodes((nds) => nds.filter((n) => n.id !== selectedId));
    setEdges((eds) => eds.filter((e) => e.source !== selectedId && e.target !== selectedId));
    setSelectedId(null);
  };

  const saveMutation = useMutation({
    mutationFn: () =>
      agentFlowsApi.updateFlow(id, {
        name: name.trim() || 'Flujo sin nombre',
        flow_config: buildFlowConfigForSave({ nodes, edges, meta: flowMeta || undefined }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agentFlows'] });
      queryClient.invalidateQueries({ queryKey: ['agentFlow', id] });
      showSuccess('Flujo guardado');
    },
    onError: (e) => showError(e?.message || 'No se pudo guardar'),
  });

  if (!isLoading && isSuccess && !flow) {
    return <Navigate to="/agent-flows" replace />;
  }

  if (isLoading || !flow || flow.id !== id) {
    return (
      <Box sx={{ width: '100%', alignSelf: 'stretch', p: 3 }}>
        <Typography color="text.secondary">Cargando editor…</Typography>
      </Box>
    );
  }

  const canvasH = `calc(100vh - ${topBarHeight}px - 140px)`;

  return (
    <Box sx={{ width: '100%', alignSelf: 'stretch', display: 'flex', flexDirection: 'column', gap: 2, pb: 2 }}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          border: `1px solid ${K.border}`,
          borderRadius: 2,
          bgcolor: '#fff',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <IconButton onClick={() => navigate('/agent-flows')} aria-label="Volver">
          <BackIcon />
        </IconButton>
        <TextField
          label="Nombre del flujo"
          value={name}
          onChange={(e) => setName(e.target.value)}
          size="small"
          sx={{ ...kobraFieldSx, minWidth: 220, flex: 1 }}
        />
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending}
          sx={kobraPrimaryBtnSx}
        >
          Guardar
        </Button>
      </Paper>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          alignItems: 'stretch',
          minHeight: 0,
          flex: 1,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: { xs: '100%', md: 340 },
            flexShrink: 0,
            p: 2,
            border: `1px solid ${K.border}`,
            borderRadius: 2,
            bgcolor: '#fff',
            maxHeight: { md: canvasH },
            overflow: 'auto',
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5, color: K.navy }}>
            Agent Builder
          </Typography>
          <AgentFlowTriggerContextPanel flowMeta={flowMeta} setFlowMeta={setFlowMeta} kobraFieldSx={kobraFieldSx} />
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" sx={{ fontWeight: 800, color: K.navy, display: 'block', mb: 1 }}>
            3–5 · Lienzo (lógica, acción, outcome)
          </Typography>
          <Stack spacing={1}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Lógica
            </Typography>
            <Button variant="outlined" size="small" onClick={() => addNode('condition')} sx={{ textTransform: 'none' }}>
              + Condición
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Acción
            </Typography>
            <Button variant="outlined" size="small" onClick={() => addNode('message')} sx={{ textTransform: 'none' }}>
              + Acción (mensaje / canal)
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Outcome
            </Typography>
            <Button variant="outlined" size="small" onClick={() => addNode('end')} sx={{ textTransform: 'none' }}>
              + Fin de flujo
            </Button>
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Conecta desde el nodo Inicio. Condición: salidas sí / no. Guarda para persistir trigger, contexto y grafo.
          </Typography>
          {selectedNode && selectedNode.type !== 'start' && (
            <Button color="error" size="small" onClick={removeSelectedNode} sx={{ textTransform: 'none' }}>
              Eliminar nodo seleccionado
            </Button>
          )}
        </Paper>

        <Paper
          elevation={0}
          sx={{
            flex: 1,
            minHeight: canvasH,
            border: `1px solid ${K.border}`,
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: alpha(K.navy, 0.02),
          }}
        >
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onSelectionChange={onSelectionChange}
            nodeTypes={agentFlowNodeTypes}
            fitView
            deleteKeyCode={['Backspace', 'Delete']}
            proOptions={{ hideAttribution: true }}
          >
            <Background gap={16} size={1} />
            <Controls />
            <MiniMap pannable zoomable />
            <Panel position="top-right">
              <Typography variant="caption" sx={{ bgcolor: '#fff', px: 1, py: 0.5, borderRadius: 1, border: `1px solid ${K.border}` }}>
                5 capas · ejecución próximamente
              </Typography>
            </Panel>
          </ReactFlow>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            width: { xs: '100%', md: 280 },
            flexShrink: 0,
            p: 2,
            border: `1px solid ${K.border}`,
            borderRadius: 2,
            bgcolor: '#fff',
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5, color: K.navy }}>
            Propiedades del nodo
          </Typography>
          <AgentFlowNodeProperties
            selectedNode={selectedNode}
            updateSelectedData={updateSelectedData}
            kobraFieldSx={kobraFieldSx}
          />
        </Paper>
      </Box>
    </Box>
  );
}

export function AgentFlowEditor() {
  return (
    <ReactFlowProvider>
      <AgentFlowEditorInner />
    </ReactFlowProvider>
  );
}
